import {
  setAdmins,
  setApiKey,
  setWebhooks,
  setScim,
  setMdm,
  setEmailPreference,
  setNotificationPreference,
  setEmailEnrollmentTemplate,
  setThreatHunterUsers,
  setAuditLogs,
  setAuditLogsTotalPages,
  setAuditLogsCurrentPage,
  setWebhookSecrets,
  setOrgHasDemoData,
  setDevicePolicies,
  setConditionalAccess,
  setConditionalAccessStatus,
  setCustomDnsConfiguration,
  setDevicePolicy,
  setMtdStatus,
} from '.'
import Server from '../../api/server'
import {
  ADMINS_ROUTE,
  RESEND_INVITE_ADMIN_ROUTE,
  ADD_ADMIN_ROUTE,
  SSO_ROUTE,
  API_ROUTE,
  WEBHOOK_ROUTE,
  SCIM_ROUTE,
  MDM_ROUTE,
  EMAIL_PREFERENCES_ROUTE,
  NOTIFICATION_PREFERENCES_ROUTE,
  ENROLLMENT_EMAIL_TEMPLATE_ROUTE,
  PUT_ENROLLMENT_EMAIL_TEMPLATE_ROUTE,
  THREAT_HUNTER_USERS_ROUTE,
  ADD_THREAT_HUNTER_USER_ROUTE,
  CONDITIONAL_ACCESS_CONFIG_ROUTE,
  CONDITIONAL_ACCESS_DELETE_ROUTE,
  DELETE_ADMIN_ROUTE,
  DEVICE_POLICY_ROUTE,
  AUDIT_ROUTE,
  UPDATE_ADMIN_ROUTE,
  SCIM_ENABLED_ROUTE,
  WEBHOOK_SECRET_ROUTE,
  RESET_DEMO_ACCOUNT_ROUTE,
  REMOVE_DEMO_DATA_ROUTE,
  CHECK_IF_ORG_HAS_DEMO_DATA_ROUTE,
  CONDITIONAL_ACCESS_STATUS_ROUTE,
  CUSTOM_DNS_ROUTE,
  DEVICE_POLICIES_ROUTE,
  DEVICE_POLICY_UPDATE_ROUTE,
  DEVICE_POLICY_CREATE_ROUTE,
  DEVICE_POLICY_UPDATE_NAME_ROUTE,
  MTD_STATUS_ROUTE,
} from '../../strings/api-consts'
import { rolesOptions } from '../../strings/dropdown-options'
import {
  AddSsoSettings,
  AddThreatHunterUser,
  Admin,
  AdminResponse,
  ApiKey,
  AuditLogFilter,
  AuditLogs,
  DevicePolicy,
  ConditionalAccessResponse,
  ConditionalAccessStatusResponse,
  EmailPreferences,
  EnrollEmailTemplate,
  FetchedWebhookRequest,
  HasDemoDataResponse,
  Mdm,
  NotificationPreferences,
  Scim,
  ScimEnabled,
  ThreatHunterUsersResponse,
  WebhookHeaders,
  WebhookPayload,
  WebhookRequest,
  WebhookSecret,
  WebhookType,
  CustomDnsConfiguration,
  DevicePolicyUpdateNameRequest,
} from '../../types/SettingsType'
import { CustomThunkDispatch } from '../../types/UserTypes'
import { parseHeaders } from '../../utils/parseHeaders'
import { reportErrorToDatadog } from '../../utils/error-logging'

const server = new Server()

export const fetchAdmins = () => async (dispatch: CustomThunkDispatch) => {
  const admins = await server.get(ADMINS_ROUTE, dispatch)
  const mappedAdmins = (admins as AdminResponse).admins.map((admin) => {
    return {
      ...admin,
      roles: rolesOptions.filter((role) => admin.roles?.includes(role.label)),
    } as Admin
  })
  dispatch(setAdmins(mappedAdmins))
}

export const resendAdminInvite = (adminInfo: Admin) => async (dispatch: CustomThunkDispatch) => {
  return server
    .post(
      RESEND_INVITE_ADMIN_ROUTE,
      {
        name: adminInfo.name,
        email: adminInfo.email,
        roles: adminInfo.roles.map((role) => role.label).join(','),
      },
      dispatch
    )
    .then(() => {
      return true
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      return false
    })
}

export const addAdmin = (adminInfo: Admin) => async (dispatch: CustomThunkDispatch) => {
  return server
    .post(
      ADD_ADMIN_ROUTE,
      {
        name: adminInfo.name,
        email: adminInfo.email,
        roles: adminInfo.roles.map((role) => role.label).join(','),
      },
      dispatch
    )
    .then(async (response) => {
      if (response?.ok) {
        return { message: 'success' }
      }
      try {
        const errorData = await response?.json()
        return { error: errorData.error || 'There was an error adding the admin, please try again' }
      } catch {
        return { error: 'There was an error adding the admin, please try again' }
      }
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      return { error: err.message }
    })
}

export const updateAdmin = (adminInfo: Admin, oldEmail: string) => async (dispatch: CustomThunkDispatch) => {
  return await server
    .patch(
      UPDATE_ADMIN_ROUTE,
      {
        name: adminInfo.name,
        email: oldEmail,
        newEmail: adminInfo.email,
        roles: adminInfo.roles.map((role) => role.label).join(','),
      },
      dispatch
    )
    .then((response) => {
      return response?.ok ?? false
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      return false
    })
}

export const deleteAdmin = (email: string) => async (dispatch: CustomThunkDispatch) => {
  return server
    .post(DELETE_ADMIN_ROUTE, { email }, dispatch)
    .then((response) => {
      return response?.ok ?? false
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      return false
    })
}

export const fetchAuditLogs =
  (
    pageNumber: number,
    pageSize: number,
    filters: AuditLogFilter,
    sortOrder?: string,
    sortColumn?: string,
    key = null,
    value = null
  ) =>
  async (dispatch: CustomThunkDispatch) => {
    let url = `${AUDIT_ROUTE}?page=${pageNumber}&pageSize=${pageSize}`
    const { fromDateTime, toDateTime } = filters
    if (sortColumn) {
      url += `&sortColumn=${sortColumn}`
      if (sortOrder) {
        url += `&sortOrder=${sortOrder}`
      }
    }

    if (fromDateTime) {
      url += `&fromDateTime=${fromDateTime}`
    }

    if (toDateTime) {
      url += `&toDateTime=${toDateTime}`
    }

    if (key && value) {
      url += `&key=${key}&value=${value}`
    }

    return await server
      .get(url, dispatch)
      .then((audits: AuditLogs) => {
        dispatch(setAuditLogsCurrentPage(!pageNumber ? 0 : pageNumber - 1))
        dispatch(setAuditLogs(audits.data))
        dispatch(setAuditLogsTotalPages(audits.totalPages))
        return true
      })
      .catch((err) => {
        reportErrorToDatadog(err.message)
        return false
      })
  }

export const applySsoSettings = (adminInfo: AddSsoSettings) => async (dispatch: CustomThunkDispatch) => {
  return server
    .post(SSO_ROUTE, adminInfo, dispatch)
    .then((response) => {
      if (response?.ok) {
        localStorage.setItem('SSO_CONFIGURED_COOKIE', 'true')
        window.location.href = import.meta.env.VITE_API_ENDPOINT + `/oauth/login?slug=${adminInfo.slug}`
        return true
      }
      return false
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      return false
    })
}

export const deleteSsoSettings = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .delete(SSO_ROUTE, dispatch)
    .then((response) => {
      return response?.ok ?? false
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      return false
    })
}

export const fetchAPIKey = () => async (dispatch: CustomThunkDispatch) => {
  await server
    .get(API_ROUTE, dispatch)
    .then((apiKey: ApiKey) => {
      dispatch(setApiKey(apiKey))
    })
    .catch((err) => {
      dispatch(setApiKey(undefined))
      reportErrorToDatadog(err.message)
      return false
    })
}

export const generateAPIKey = (expiration: string) => async (dispatch: CustomThunkDispatch) => {
  await server
    .post(API_ROUTE, { tokenExpiration: expiration }, dispatch)
    .then((res) => {
      if (!res) {
        throw new Error('Response is undefined')
      }
      return res.json()
    })
    .then((apiKey: ApiKey) => {
      dispatch(setApiKey(apiKey))
    })
    .catch((err) => {
      dispatch(setApiKey(undefined))
      reportErrorToDatadog(err.message)
      return false
    })
}

export const revokeAPIKey = () => async (dispatch: CustomThunkDispatch) => {
  await server
    .delete(API_ROUTE, dispatch)
    .then(() => {
      dispatch(setApiKey(undefined))
    })
    .catch((err) => {
      dispatch(setApiKey(undefined))
      reportErrorToDatadog(err.message)
      return false
    })
}

export const fetchWebhook = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .get(WEBHOOK_ROUTE, dispatch)
    .then((webhooks: FetchedWebhookRequest[]) => {
      const webhookPayloads: WebhookPayload[] = webhooks.map((webhook) => ({
        ...webhook,
        headers: parseHeaders(webhook.headers) as WebhookHeaders,
      }))
      dispatch(setWebhooks(webhookPayloads))
      return webhookPayloads
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      return undefined
    })
}

export const fetchWebhookSecrets = (missingWebhookTypes: WebhookType[]) => async (dispatch: CustomThunkDispatch) => {
  await server
    .post(WEBHOOK_SECRET_ROUTE, { missingWebhookTypes: missingWebhookTypes }, dispatch)
    .then((res) => {
      if (!res) {
        throw new Error('Response is undefined')
      }
      return res.json()
    })
    .then((apiKey: WebhookSecret) => {
      dispatch(setWebhookSecrets(apiKey))
    })
    .catch((err) => {
      dispatch(setWebhookSecrets(undefined))
      reportErrorToDatadog(err.message)
      return false
    })
}

export const saveWebhook = (payload: WebhookRequest) => async (dispatch: CustomThunkDispatch) => {
  return server
    .post(WEBHOOK_ROUTE, payload, dispatch)
    .then((res) => {
      if (res?.status === 200) {
        dispatch(setWebhooks(payload))
        return { success: res.ok, message: 'Webhook has been successfully set up' }
      }
      return res?.text().then((body) => {
        return { success: res.ok, message: body }
      })
    })
    .catch((err) => {
      return { success: false, message: err.message }
    })
}

export const deactivateWebhook = (type: WebhookType) => async (dispatch: CustomThunkDispatch) => {
  return server
    .delete(WEBHOOK_ROUTE, dispatch, { webhookType: type })
    .then(() => {
      dispatch(setWebhooks(undefined))
    })
    .catch(() => {
      dispatch(setWebhooks(undefined))
    })
}

export const fetchScimEnabled = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .get(SCIM_ENABLED_ROUTE, dispatch)
    .then((result: ScimEnabled) => {
      dispatch(setScim({ enabled: result.enabled, scimUrl: '', authHeader: '' }))
    })
    .catch(() => {
      dispatch(setScim({ enabled: false, scimUrl: '', authHeader: '' }))
    })
}

export const deleteScim = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .delete(SCIM_ROUTE, dispatch)
    .then((res) => {
      if (!res) {
        throw new Error('Response is undefined')
      }
      return res.json()
    })
    .then((scim: Scim) => {
      dispatch(setScim(scim))
    })
    .catch(() => {
      dispatch(setScim({ enabled: false, scimUrl: '', authHeader: '' }))
    })
}

export const fetchMdmStatus = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .get(MDM_ROUTE, dispatch)
    .then((mdm: Mdm) => {
      dispatch(setMdm(mdm))
    })
    .catch(() => {
      dispatch(setMdm({ enabled: false, token: '' }))
    })
}

export const updateMdmStatus = (status: boolean) => async (dispatch: CustomThunkDispatch) => {
  return server
    .post(MDM_ROUTE, { enable: status }, dispatch)
    .then((res) => {
      if (!res) {
        throw new Error('Response is undefined')
      }
      return res.json()
    })
    .then((mdm: Mdm) => {
      dispatch(setMdm(mdm))
    })
    .catch(() => {
      dispatch(setMdm({ enabled: false, token: '' }))
    })
}

export const fetchEmailPreferences = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .get(EMAIL_PREFERENCES_ROUTE, dispatch)
    .then((emailPreferences: EmailPreferences) => {
      dispatch(setEmailPreference(emailPreferences))
    })
    .catch(() => {
      dispatch(setEmailPreference(undefined))
    })
}

export const fetchThreatHunterUsers = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .get(THREAT_HUNTER_USERS_ROUTE, dispatch)
    .then((threatHunterUsers: ThreatHunterUsersResponse) => {
      dispatch(setThreatHunterUsers(threatHunterUsers.threatHunterUsers))
      return threatHunterUsers
    })
    .catch((error) => {
      dispatch(setThreatHunterUsers(undefined))
      return { error: error }
    })
}

export const fetchNotificationPreferences = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .get(NOTIFICATION_PREFERENCES_ROUTE, dispatch)
    .then((notificationPreferences: NotificationPreferences) => {
      dispatch(setNotificationPreference(notificationPreferences))
    })
    .catch(() => {
      dispatch(setNotificationPreference(undefined))
    })
}

export const postCustomDnsConfiguration =
  (payload: CustomDnsConfiguration) => async (dispatch: CustomThunkDispatch) => {
    return server
      .post(CUSTOM_DNS_ROUTE, payload, dispatch)
      .then((res) => {
        dispatch(setCustomDnsConfiguration(payload))
        return { success: res?.status == 200, data: res }
      })
      .catch((error) => {
        dispatch(setCustomDnsConfiguration(undefined))
        return { error: error.message, success: false }
      })
  }

export const fetchCustomDnsConfiguration = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .get(CUSTOM_DNS_ROUTE, dispatch)
    .then((customDnsConfiguration: CustomDnsConfiguration) => {
      dispatch(setCustomDnsConfiguration(customDnsConfiguration))
      return { success: true, data: customDnsConfiguration }
    })
    .catch((error) => {
      dispatch(setCustomDnsConfiguration(undefined))
      return { error: error.message, success: false }
    })
}

export const addThreatHunterUser = (user: AddThreatHunterUser) => async (dispatch: CustomThunkDispatch) => {
  return server
    .post(ADD_THREAT_HUNTER_USER_ROUTE, user, dispatch)
    .then((res) => {
      if (!res) {
        throw new Error('Response is undefined')
      }
      return res.json()
    })
    .then((threatHunterUsers: ThreatHunterUsersResponse) => {
      dispatch(setThreatHunterUsers(threatHunterUsers.threatHunterUsers))
      return threatHunterUsers
    })
    .catch((error) => {
      dispatch(setThreatHunterUsers(undefined))
      return { error: error }
    })
}

export const putEmailPreferences = (payload: EmailPreferences) => async (dispatch: CustomThunkDispatch) => {
  return server
    .put(EMAIL_PREFERENCES_ROUTE, payload, dispatch)
    .then(() => {
      return true
    })
    .catch(() => {
      return false
    })
}

export const putNotificationPreferences =
  (payload: NotificationPreferences) => async (dispatch: CustomThunkDispatch) => {
    return server
      .put(NOTIFICATION_PREFERENCES_ROUTE, payload, dispatch)
      .then(() => {
        return true
      })
      .catch(() => {
        return false
      })
  }

export const fetchEnrollmentEmailTemplate = () => async (dispatch: CustomThunkDispatch) => {
  await server
    .get(ENROLLMENT_EMAIL_TEMPLATE_ROUTE, dispatch)
    .then((enrollEmailTemplate: EnrollEmailTemplate) => {
      dispatch(setEmailEnrollmentTemplate(enrollEmailTemplate))
    })
    .catch(() => {
      dispatch(setEmailEnrollmentTemplate(undefined))
    })
}

export const putEnrollmentEmailTemplate = (payload: EnrollEmailTemplate) => async (dispatch: CustomThunkDispatch) => {
  return server
    .put(PUT_ENROLLMENT_EMAIL_TEMPLATE_ROUTE, payload, dispatch)
    .then(() => {
      return true
    })
    .catch(() => {
      return false
    })
}

export const fetchConditionalAccessConfig = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .get(CONDITIONAL_ACCESS_CONFIG_ROUTE, dispatch)
    .then((conditionalAccess?: ConditionalAccessResponse) => {
      dispatch(setConditionalAccess(conditionalAccess))
      dispatch(setConditionalAccessStatus(conditionalAccess?.active))
      return conditionalAccess
    })
    .catch(() => {
      return undefined
    })
}

export const fetchConditionalAccessStatus = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .get(CONDITIONAL_ACCESS_STATUS_ROUTE, dispatch)
    .then((response: ConditionalAccessStatusResponse) => {
      dispatch(setConditionalAccessStatus(response.status))
      return response.status
    })
    .catch((error) => {
      console.error('error', error)
      return undefined
    })
}

export const fetchMTDStatus = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .get(MTD_STATUS_ROUTE, dispatch)
    .then((response: ConditionalAccessStatusResponse) => {
      dispatch(setMtdStatus(response.status))
      return response.status
    })
    .catch((error) => {
      console.error('error', error)
      dispatch(setMtdStatus(undefined))
      return undefined
    })
}

export const postConditionalAccessConfig =
  (payload: ConditionalAccessResponse) => async (dispatch: CustomThunkDispatch) => {
    return server
      .post(CONDITIONAL_ACCESS_CONFIG_ROUTE, payload, dispatch)
      .then((res) => {
        if (res?.status === 202) {
          return { error: undefined }
        }
        return res?.json().then((data) => {
          if (!res.ok) {
            return { error: data.error || 'Unknown error' }
          } else {
            return { error: undefined }
          }
        })
      })
      .catch((error) => {
        return { error: error.message }
      })
  }

export const fetchDevicePolicies = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .get(DEVICE_POLICIES_ROUTE, dispatch)
    .then((policies: DevicePolicy[]) => {
      let devicePolicies = undefined
      if (policies) {
        const policyArray = Array.isArray(policies) ? policies : [policies]
        devicePolicies = policyArray.map((policy) => ({
          ...policy,
          screenLockEnabled: policy.screenLockEnabled.toString(),
          smishingEnabled: policy.smishingEnabled.toString(),
          biometricsEnabled: policy.biometricsEnabled.toString(),
          travelEnabled: policy.travelEnabled?.toString() || 'false',
        }))
      }
      dispatch(setDevicePolicies(devicePolicies))
      return devicePolicies
    })
    .catch(() => {
      return undefined
    })
}

export const fetchDevicePolicy = (policyId: number) => async (dispatch: CustomThunkDispatch) => {
  return server
    .post(DEVICE_POLICY_ROUTE, { policyId: policyId }, dispatch)
    .then((res) => {
      if (!res) {
        throw new Error('Response is undefined')
      }
      return res.json()
    })
    .then((policy: DevicePolicy) => {
      let devicePolicy = undefined
      if (policy) {
        devicePolicy = {
          ...policy,
          screenLockEnabled: policy.screenLockEnabled.toString(),
          smishingEnabled: policy.smishingEnabled.toString(),
          biometricsEnabled: policy.biometricsEnabled.toString(),
          travelEnabled: policy.travelEnabled?.toString() || 'false',
        }
      }
      dispatch(setDevicePolicy(devicePolicy))
      return devicePolicy
    })
    .catch(() => {
      return undefined
    })
}

export const postUpdateDevicePolicy = (payload: DevicePolicy) => async (dispatch: CustomThunkDispatch) => {
  const updatedPayload = {
    ...payload,
    screenLockEnabled: payload.screenLockEnabled === 'true' ? true : false,
    smishingEnabled: payload.smishingEnabled === 'true' ? true : false,
    biometricsEnabled: payload.biometricsEnabled === 'true' ? true : false,
  }

  return server
    .post(DEVICE_POLICY_UPDATE_ROUTE, updatedPayload, dispatch)
    .then((res) => {
      if (!res) {
        throw new Error('Response is undefined')
      }
      return true
    })
    .catch(() => {
      return false
    })
}

export const postCreateDevicePolicy = (payload: DevicePolicy) => async (dispatch: CustomThunkDispatch) => {
  const updatedPayload = {
    ...payload,
    policyId: 0,
    screenLockEnabled: payload.screenLockEnabled === 'true' ? true : false,
    smishingEnabled: payload.smishingEnabled === 'true' ? true : false,
    biometricsEnabled: payload.biometricsEnabled === 'true' ? true : false,
  }

  return server
    .post(DEVICE_POLICY_CREATE_ROUTE, updatedPayload, dispatch)
    .then((res) => {
      if (!res) {
        throw new Error('Response is undefined')
      }
      return res.json()
    })
    .then((policy: DevicePolicy) => {
      return policy
    })
    .catch(() => {
      return undefined
    })
}

export const postUpdateDevicePolicyName =
  (payload: DevicePolicyUpdateNameRequest) => async (dispatch: CustomThunkDispatch) => {
    return server
      .post(DEVICE_POLICY_UPDATE_NAME_ROUTE, payload, dispatch)
      .then((res) => {
        if (!res) {
          throw new Error('Response is undefined')
        }
        return true
      })
      .catch(() => {
        return false
      })
  }

export const deleteDevicePolicy = (policyId: number) => async (dispatch: CustomThunkDispatch) => {
  return server
    .delete(DEVICE_POLICY_ROUTE, dispatch, { policyId: policyId })
    .then(() => {
      return true
    })
    .catch(() => {
      return false
    })
}

export const deleteConditionalAccess = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .delete(CONDITIONAL_ACCESS_DELETE_ROUTE, dispatch)
    .then(() => {
      return true
    })
    .catch(() => {
      return false
    })
}

export const postDisableConditionalAccess =
  (payload: ConditionalAccessResponse) => async (dispatch: CustomThunkDispatch) => {
    return server
      .post(CONDITIONAL_ACCESS_CONFIG_ROUTE, payload, dispatch)
      .then(() => {
        return true
      })
      .catch(() => {
        return false
      })
  }

export const resetDemoAccount = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .post(RESET_DEMO_ACCOUNT_ROUTE, {}, dispatch)
    .then((res) => {
      if (res?.status === 200) {
        return true
      }
      return false
    })
    .catch(() => {
      return false
    })
}

export const removeDemoData = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .post(REMOVE_DEMO_DATA_ROUTE, {}, dispatch)
    .then((res) => {
      if (res?.status === 200) {
        return true
      }
      return false
    })
    .catch(() => {
      return false
    })
}

export const checkIfOrgHasDemoData = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .get(CHECK_IF_ORG_HAS_DEMO_DATA_ROUTE, dispatch)
    .then((hasDemoData: HasDemoDataResponse) => {
      dispatch(setOrgHasDemoData(hasDemoData.orgHasDemoData))
      return hasDemoData
    })
    .catch(() => {
      dispatch(setOrgHasDemoData(false))
      return { orgHasDemoData: false }
    })
}
